/**
 * Entwicklereinstellungen — single page with a left-rail sub-menu, mirrors
 * the regular SettingsLayout pattern. Houses everything an admin needs
 * but that customers should never see:
 *
 *   - Mitgliedschafts-Pläne   (custom catalogue, source-of-truth synced from Pricing)
 *   - Platform-Keys           (Mollie, Generalkeys, pricing, tax)
 *   - Nummern-Pool            (platform-owned DIDs)
 *   - Rechnungs-Einstellungen (PDF + X-Rechnung + scheduled send)
 *   - Rechnungen              (issued invoices, all customers)
 */
import * as React from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';

interface MenuItem {
  slug: string;
  label: string;
}

const MENU: ReadonlyArray<MenuItem> = [
  { slug: 'runtime', label: 'System-Modus' },
  { slug: 'membership-plans', label: 'Mitgliedschafts-Pläne' },
  { slug: 'agent-presets', label: 'Agent-Presets' },
  { slug: 'onboarding', label: 'Onboarding-Konfig' },
  { slug: 'platform-keys', label: 'Platform-Keys' },
  { slug: 'numbers', label: 'Nummern-Pool' },
  { slug: 'invoice-settings', label: 'Rechnungs-Einstellungen' },
  { slug: 'invoices', label: 'Rechnungen' },
  { slug: 'mail-broadcast', label: 'Mail-Versand' },
  { slug: 'reconciliation', label: 'Reconciliation' },
];

export function AdminDevSettingsLayout() {
  return (
    <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
      <aside className="lg:sticky lg:top-20 lg:self-start">
        <nav
          aria-label="Entwicklereinstellungen"
          className="-mx-1 flex gap-1 overflow-x-auto pb-1 lg:hidden"
        >
          {MENU.map((m) => (
            <MenuPill key={m.slug} item={m} />
          ))}
        </nav>
        <nav
          aria-label="Entwicklereinstellungen"
          className="hidden flex-col gap-0.5 lg:flex"
        >
          {MENU.map((m) => (
            <MenuRow key={m.slug} item={m} />
          ))}
        </nav>
      </aside>
      <div className="min-w-0 space-y-4">
        <Outlet />
      </div>
    </div>
  );
}

function MenuRow({ item }: { item: MenuItem }) {
  const { pathname } = useLocation();
  const to = `/admin/dev/${item.slug}`;
  const active = pathname === to || pathname.startsWith(`${to}/`);
  return (
    <NavLink
      to={to}
      className={cn(
        'rounded-md px-3 py-2 text-sm font-medium transition-colors',
        active
          ? 'bg-secondary text-foreground'
          : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground',
      )}
    >
      {item.label}
    </NavLink>
  );
}

function MenuPill({ item }: { item: MenuItem }) {
  const { pathname } = useLocation();
  const to = `/admin/dev/${item.slug}`;
  const active = pathname === to || pathname.startsWith(`${to}/`);
  return (
    <NavLink
      to={to}
      className={cn(
        'shrink-0 rounded-md border px-3 py-1.5 text-sm font-medium',
        active
          ? 'border-border bg-secondary text-foreground'
          : 'border-transparent text-muted-foreground',
      )}
    >
      {item.label}
    </NavLink>
  );
}
